import { z } from "zod";

import type { PlanningAdminChatResponse, PlanningSchedule } from "./models";

const nonblank = z.string().trim().min(1);
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(value => {
    const date = new Date(`${value}T00:00:00Z`);
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}, "Invalid calendar date");
const clock = z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/);

const assignmentInput = z.object({
    assignment_id: nonblank.optional(),
    logical_assignment_id: nonblank.optional(),
    source_request_id: nonblank.optional(),
    source_task_id: nonblank.optional(),
    parent_shift_id: nonblank.optional(),
    employee_id: nonblank.optional(),
    employee_name: nonblank.optional(),
    task_type: nonblank,
    date: isoDate,
    start_time: clock,
    end_time: clock,
    client_id: nonblank.optional(),
    client_name: nonblank.optional(),
    location_id: z.string().optional(),
    notes: z.string().optional(),
    description: z.string().optional()
}).strict().refine(value => Boolean(value.employee_id || value.employee_name), "An employee is required");

const selectorShape = {
    assignment_id: nonblank.optional(),
    logical_assignment_id: nonblank.optional(),
    employee_id: nonblank.optional(),
    employee_name: nonblank.optional(),
    date: isoDate.optional(),
    start_time: clock.optional(),
    end_time: clock.optional(),
    task_type: nonblank.optional()
};
const selector = z.object(selectorShape).strict().refine(
    value => Boolean(value.assignment_id || value.logical_assignment_id || ((value.employee_id || value.employee_name) && value.date)),
    "An assignment selector is required"
);
const changes = z.object({
    employee_id: nonblank.optional(),
    employee_name: nonblank.optional(),
    task_type: nonblank.optional(),
    date: isoDate.optional(),
    start_time: clock.optional(),
    end_time: clock.optional(),
    client_id: z.string().optional(),
    client_name: z.string().optional(),
    location_id: z.string().optional(),
    notes: z.string().optional(),
    description: z.string().optional(),
    parent_shift_id: z.string().optional()
}).strict().refine(value => Object.keys(value).length > 0, "Specify the assignment changes");

export const planningAdminChatResponseSchema: z.ZodType<PlanningAdminChatResponse> = z.object({
    reply: nonblank,
    action: z.enum(["none", "approve", "reject", "reject_and_generate", "generate", "publish", "preview", "refresh",
        "add_assignment", "edit_assignment", "delete_assignment"]),
    request_ids: z.array(nonblank).optional(),
    start_date: isoDate.optional(),
    end_date: isoDate.optional(),
    adjustment_instruction: nonblank.optional(),
    fixed_assignments: z.array(assignmentInput).optional(),
    add_assignments: z.array(assignmentInput).optional(),
    edit_assignment: z.object({ ...selectorShape, updates: changes }).strict().refine(
        value => Boolean(value.assignment_id || value.logical_assignment_id || ((value.employee_id || value.employee_name) && value.date)),
        "An assignment selector is required"
    ).optional(),
    delete_assignment: selector.optional()
}).strict().superRefine((value, context) => {
    if (Boolean(value.start_date) !== Boolean(value.end_date) || (value.start_date && value.end_date && value.start_date > value.end_date)) {
        context.addIssue({ code: "custom", path: ["start_date"], message: "Specify a complete, ordered date interval" });
    }
    const missing = value.action === "add_assignment" ? !value.add_assignments?.length
        : value.action === "edit_assignment" ? !value.edit_assignment
        : value.action === "delete_assignment" ? !value.delete_assignment
        : ["approve", "reject", "reject_and_generate"].includes(value.action) ? !value.request_ids?.length : false;
    if (missing) context.addIssue({ code: "custom", path: ["action"], message: "The scheduling action is missing its data" });
});

const optionalText = z.string().nullable().optional();
export const planningScheduleSchema: z.ZodType<PlanningSchedule, z.ZodTypeDef, unknown> = z.object({
    id: nonblank,
    schedule_id: nonblank,
    company_id: nonblank,
    location_id: z.string().optional(),
    start_date: z.string(),
    end_date: z.string(),
    status: z.enum(["draft", "published", "archived"]),
    health: z.enum(["green", "yellow", "red"]),
    revision: z.number().int().positive().optional(),
    source_schedule_id: optionalText,
    source_schedule_revision: z.number().int().positive().nullable().optional(),
    assignments: z.array(z.object({
        assignment_id: nonblank, logical_assignment_id: optionalText, parent_shift_id: optionalText, replaces_assignment_id: optionalText,
        source_request_id: optionalText, source_task_id: optionalText, description: optionalText,
        employee_id: z.string(), employee_name: z.string(), task_type: z.string(),
        date: z.string(), start_time: z.string(), end_time: z.string(),
        client_id: optionalText, client_name: optionalText, location_id: optionalText,
        notes: optionalText, ai_brief: optionalText, reason_codes: z.array(z.string()).default([])
    }).passthrough()),
    violations: z.array(z.object({
        violation_type: nonblank, severity: z.enum(["error", "warning"]), message: z.string(),
        related_assignment_id: optionalText, related_task_id: optionalText
    }).passthrough()),
    unassigned_tasks: z.array(z.object({
        request_id: optionalText, task_id: optionalText, task_description: z.string(), reason: z.string(), suggestion: optionalText
    }).passthrough()),
    created_by: z.string(), created_at: z.string(),
    published_at: optionalText, published_by: optionalText, ai_explanation: optionalText, type: z.string().optional()
}).passthrough();

export const planningScheduleListSchema = z.array(planningScheduleSchema);

export class PlanningApiError extends Error {
    constructor(message: string, readonly status: number, readonly code?: string, readonly schedule?: PlanningSchedule) {
        super(message);
        this.name = "PlanningApiError";
    }
}

export async function ensurePlanningResponseOk(response: Response): Promise<void> {
    if (response.ok) return;
    const body: unknown = await response.json().catch(() => null);
    const error = z.object({
        error: z.string().optional(), message: z.string().optional(), code: z.string().optional(),
        schedule: z.unknown().optional()
    }).safeParse(body);
    const message = error.success ? error.data.error?.trim() || error.data.message?.trim() : undefined;
    const schedule = error.success ? planningScheduleSchema.safeParse(error.data.schedule) : null;
    throw new PlanningApiError(message || `Scheduling request failed (${response.status}${response.statusText ? ` ${response.statusText}` : ""}).`,
        response.status, error.success ? error.data.code : undefined, schedule?.success ? schedule.data : undefined);
}

export async function readPlanningResponse<T>(
    response: Response,
    schema: z.ZodType<T, z.ZodTypeDef, unknown>
): Promise<T> {
    await ensurePlanningResponseOk(response);
    const body: unknown = await response.json();
    const result = schema.safeParse(body);
    if (!result.success) throw new PlanningApiError("The scheduling response is invalid. Refresh before trying again.", response.status, "PLANNING_RESPONSE_INVALID");
    return result.data;
}
